import axios from 'axios'
import { groupBy, sortBy, sortedUniq } from 'lodash-es'
import { EBO_API } from '~/utils/constants'
import weeksJson from '~/data/nfl-weeks.json'


const stats = axios.create({
  baseURL: `${EBO_API}/sports/v3/nfl/stats/json`,
  timeout: 60000,
})
const scores = axios.create({
  baseURL: `${EBO_API}/sports/v3/nfl/scores/json`,
  timeout: 60000,
})
const headshots = axios.create({
  baseURL: `${EBO_API}/sports/v3/nfl/headshots/json/Headshots`,
  timeout: 60000,
})


let currentSeason
export async function getCurrentSeasonYear() {
  if (!currentSeason) {
    currentSeason = await scores.get('/CurrentSeason').then(({ data }) => parseInt(data))
  }
  return currentSeason
}

let currentYear
export async function getCurrentYear() {
  if (!currentYear) {
    const year = await getCurrentSeasonYear()
    currentYear = await isCurrentSeasonInProgress() ? year : year - 1
  }
  return currentYear
}

let currentSeasonInProgress
export async function isCurrentSeasonInProgress() {
  if (currentSeasonInProgress === undefined) {
    const currentSeasonYear = await getCurrentSeasonYear()
    const { data } = await scores.get(`/Scores/${currentSeasonYear}PRE`)
    currentSeasonInProgress = Boolean(data.length > 0 && data.find(game => game.HomeScore))
  }
  return currentSeasonInProgress
}

let weeks
export async function getWeeks () {
  if (!weeks) {
    const currentYear = await getCurrentYear()
    const data = await scores.get(`Timeframes/all`)
      .then(res => groupBy(res.data, 'Season'))
      .then(data => Object.values(data)
        .filter(weeks => weeks[0].Season > 2023 && weeks[0].Season <= currentYear)
        .map(weeks => ({
          year: weeks[0].Season,
          weeks: weeks
            .filter(week => Number.isInteger(week.Week) && ['REG', 'PRE'].some(type => week.ApiSeason.includes(type)))
            .map(week => ({
              week: week.Week.toString(),
              name: week.Name,
              seasonType: week.ApiSeason.replace(week.Season.toString(), ''),
            }))
        }))
      )
    weeks = [...weeksJson, ...data]
  }
  return weeks
}

let teams
export async function getTeams() {
  if (!teams) {
    teams = await scores.get('/AllTeams').then(({ data }) => data)
  }
  return teams
}

let activeTeams
export function getActiveTeams() {
  if (!activeTeams) {
    activeTeams = scores.get('/Teams').then(({ data }) => data)
  }
  return activeTeams
}

export async function getTeam(teamId) {
  const teams = await getTeams()
  return teams.find((team) => team.TeamID === teamId) || {}
}

export async function getPlayerDetails(playerId, withTeam = true) {
  const { data } = await stats.get(`/Player/${playerId}`)
  if (withTeam) {
    data.Team = await getTeam(data.TeamID)
  }
  data.PhotoUrl = await getPlayerHeadshot(playerId) || data.PhotoUrl
  return data
}

let playerHeadshots
export const getPlayerHeadshot = async (playerId) => {
  if (!playerHeadshots) {
    playerHeadshots = await headshots.get('').then(({ data }) => data)
  }
  return playerHeadshots.find((headshot) => parseInt(headshot.PlayerID) === parseInt(playerId))?.PreferredHostedHeadshotUrl
}

export async function getSeasonGames(season, withStandings) {
  const games = await scores.get(`/Scores/${season}`).then(({ data }) => data)

  if (withStandings) {
    const runningStandings = Object.create(null)
    const playingTeams = games.flatMap((game) => [game.HomeTeamID, game.AwayTeamID])
    for (const teamID of playingTeams) {
      runningStandings[teamID] = {
        wins: 0,
        losses: 0,
        ties: 0,
      }
    }
    for (const game of games) {
      if (!game.HasStarted) continue

      if (game.HomeScore > game.AwayScore) {
        runningStandings[game.HomeTeamID].wins++
        runningStandings[game.AwayTeamID].losses++
      } else if (game.HomeScore < game.AwayScore) {
        runningStandings[game.HomeTeamID].losses++
        runningStandings[game.AwayTeamID].wins++
      } else {
        runningStandings[game.HomeTeamID].ties++
        runningStandings[game.AwayTeamID].ties++
      }

      game.HomeWins = runningStandings[game.HomeTeamID].wins
      game.AwayWins = runningStandings[game.AwayTeamID].wins
      game.HomeLosses = runningStandings[game.HomeTeamID].losses
      game.AwayLosses = runningStandings[game.AwayTeamID].losses
      game.HomeTies = runningStandings[game.HomeTeamID].ties
      game.AwayTies = runningStandings[game.AwayTeamID].ties
    }
  }

  return games
}

export function getByes(season) {
  return scores.get(`/Byes/${season}`).then(({ data }) => data)
}

export async function groupedSeasonGames(season) {
  const gameData = await getSeasonGames(season)
  const teams = await getTeams()
  const blank = (name) => ({ WikipediaLogoUrl: '', Name: name })
  gameData.forEach((game) => {
    game.HomeTeam =
      teams.find((team) => team.TeamID === game.HomeTeamID) || blank(game.HomeTeam)
    game.AwayTeam =
      teams.find((team) => team.TeamID === game.AwayTeamID) || blank(game.AwayTeam)
  })

  const sortedWeeks = sortedUniq(gameData.map(({ Week }) => Week).sort())
  return sortedWeeks.map((weekId) => {
    const weekGames = gameData.filter((game) => game.Week === weekId)
    const byDay = groupBy(weekGames, 'Day')
    const daysInOrder = sortBy(Object.keys(byDay), Date.parse)
    return {
      weekId,
      days: daysInOrder.map((day) => ({
        day: new Date(day).toLocaleString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        }),
        games: byDay[day],
      })),
    }
  })
}

export async function getTeamSeasonStats(season) {
  const { data } = await scores.get(`/TeamSeasonStats/${season}`)
  const teams = await getTeams()
  data.forEach((team) => (team.TeamData = teams.find((t) => t.TeamID === team.TeamID)))
  return data
}

export function getPlayerSeasonStats(season) {
  return stats.get(`/PlayerSeasonStats/${season}`).then(({ data }) => data)
}

export function getPlayerSeasonStatsByTeam(season, teamShortName) {
  return stats
    .get(`/PlayerSeasonStatsByTeam/${season}/${teamShortName}`)
    .then(({ data }) => data)
}

export function getPlayerSeasonStatsByPlayer(season, playerID) {
  return getPlayerSeasonStats(season)
    .then(data => data.find((p) => parseInt(p.PlayerID) === parseInt(playerID)))
}

export async function getSeasonStandings(season) {
  // if (season === currentSeasonYear && !(await isCurrentSeasonInProgress())) season = parseInt(season) - 1
  const { data } = await scores.get(`/Standings/${season}`)
  const teams = await getTeams()
  data.forEach((team) => (team.TeamData = teams.find((t) => t.TeamID === team.TeamID)))
  return data
}


let lastTimeframeOfPreviousSeasonCached
export async function getLastTimeframeOfPreviousSeason() {
  if (!lastTimeframeOfPreviousSeasonCached) {
    const current = await scores.get('/Timeframes/current').then(({ data }) => data[0])
    const recent = await scores.get('/Timeframes/recent').then(({ data }) => data)
    const currentIndex = recent.findIndex((timeframe) => timeframe.StartDate === current.StartDate)
    lastTimeframeOfPreviousSeasonCached = recent.slice(0, currentIndex + 1).filter(timeframe => timeframe.SeasonType === 1).pop()
  }
  return lastTimeframeOfPreviousSeasonCached
}

export async function getCurrentSeason() {
  return await isCurrentSeasonInProgress()
    ? scores.get('/Timeframes/current').then(({ data }) => data[0].ApiSeason)
    : getLastTimeframeOfPreviousSeason().then(({ ApiSeason }) => ApiSeason)
}

export async function getCurrentWeek() {
  return await isCurrentSeasonInProgress()
    ? scores.get('/Timeframes/current').then(({ data }) => data[0].ApiWeek)
    : getLastTimeframeOfPreviousSeason().then(({ ApiWeek }) => ApiWeek)
}

export function getScoresByWeek(season, week) {
  return scores.get(`/ScoresBasic/${season}/${week}`).then(({ data }) => data)
}

export async function makeLiveUpdating(games) {
  const now = await scores.get('/Timeframes/current').then(({ data }) => data[0])
  const toUpdate = games.filter((game) => game.IsInProgress)
  if (!toUpdate.length) return () => {}

  const interval = setInterval(async () => {
    const newScores = await getScoresByWeek(now.ApiSeason, now.ApiWeek)
    toUpdate.forEach((game) => {
      const score = newScores.find((score) => score.ScoreID === game.ScoreID)
      game.AwayScore = score.AwayScore
      game.HomeScore = score.HomeScore
    })
  }, 5000)

  return () => clearInterval(interval)
}
